import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";

export const STAT_LABELS = {
  controlWardsPlaced: "Balises de controle posees",
  damageDealtToBuildings: "Degats aux batiments",
  damageDealtToObjectives: "Degats aux objectifs",
  damageDealtToTurrets: "Degats aux tourelles",
  damageSelfMitigated: "Degats auto-mitiges",
  deaths: "Morts",
  effectiveHealAndShielding: "Soins et boucliers effectifs",
  enemyChampionImmobilizations: "Immobilisations d'ennemis",
  goldEarned: "Or gagne",
  immobilizeAndKillWithAlly: "Immobilisation + kill avec un allie",
  killAfterHiddenWithAlly: "Kill apres camouflage avec un allie",
  killParticipation: "Participation aux kills",
  magicDamageDealtToChampions: "Degats magiques",
  physicalDamageDealtToChampions: "Degats physiques",
  pickKillWithAlly: "Kill cible avec un allie",
  trueDamageDealtToChampions: "Degats bruts",
  skillshotsDodged: "Skillshots esquives",
  skillshotsHit: "Skillshots touches",
  soloKills: "Kills solo",
  timeCCingOthers: "Temps de controle de foule",
  totalAllyJungleMinionsKilled: "Monstres allies de jungle tues",
  totalDamageDealt: "Degats totaux infliges",
  totalDamageDealtToChampions: "Degats aux champions",
  totalDamageShieldedOnTeammates: "Boucliers appliques aux allies",
  totalDamageTaken: "Degats subis",
  totalEnemyJungleMinionsKilled: "Monstres ennemis de jungle tues",
  totalHeal: "Soins totaux",
  totalHealsOnTeammates: "Soins appliques aux allies",
  totalMinionsKilled: "Sbires tues",
  totalTimeCCDealt: "Duree totale de CC inflige",
  turretKills: "Tourelles detruites",
  wardsGuarded: "Balises protegees",
  wardsKilled: "Balises ennemies detruites",
  wardsPlaced: "Balises posees",
  earlyTakedowns: "Kills/assists early",
  earlyCs10: "CS a 10 min",
  earlyCsLead: "Avance CS max",
  earlyLevelLead: "Avance niveau max",
  earlyPlatesTaken: "Plaques prises",
  killsOnLanersEarlyJungleAsJungler: "Ganks early du jungler",
  killsOnOtherLanesEarlyJungleAsLaner: "Kills early hors lane",
};

function getBarColor(value) {
  const v = Number(value) || 0;
  const clamped = Math.max(-50, Math.min(50, v));
  const t = Math.abs(clamped) / 50;

  if (clamped < 0) {
    if (t < 0.33) return "#fca5a5";
    if (t < 0.66) return "#f87171";
    return "#dc2626";
  }

  if (clamped > 0) {
    if (t < 0.33) return "#bbf7d0";
    if (t < 0.66) return "#4ade80";
    return "#16a34a";
  }

  return "#64748b";
}

function formatPercent(value, maxDecimals = 2) {
  const num = Number(value) || 0;
  return `${num.toFixed(maxDecimals).replace(/\.?0+$/, "")}%`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const statKey = item.payload.statKey;
  const value = Number(item.value) || 0;
  const labelText = STAT_LABELS[statKey] || label || statKey;

  return (
    <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100 shadow-lg">
      <div className="font-semibold mb-1">{labelText}</div>
      <div className="flex items-center gap-1">
        <span className={`font-semibold ${value >= 0 ? "text-emerald-400" : "text-red-400"}`}>
          {value > 0 ? "+" : ""}
          {formatPercent(value)}
        </span>
        <span className="text-slate-400">vs ton vis-a-vis</span>
      </div>
    </div>
  );
};

const ChartImpact = ({ data }) => {
  const chartData = (data || []).map((d) => ({
    name: STAT_LABELS[d.stat] || d.stat,
    value: Number(d.value) || 0,
    statKey: d.stat,
  }));

  return (
    <div className="w-full h-[380px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 10, right: 20, bottom: 10, left: 120 }}
        >
          <XAxis
            type="number"
            stroke="#94a3b8"
            tick={{ fontSize: 11 }}
            domain={[-50, 50]}
            tickFormatter={(v) => formatPercent(v)}
          />
          <YAxis
            type="category"
            dataKey="name"
            stroke="#94a3b8"
            tick={{ fontSize: 11 }}
            width={110}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#64748b" strokeDasharray="3 3" />

          <Bar dataKey="value" radius={[4, 4, 4, 4]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.value)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ChartImpact;
